<script setup>
import { useActivityStore } from "@/stores/useActivityStore";
import { useUserStore } from "@/stores/useUserStore";
import { useGroupStore } from "@/stores/useGroupStore";
import { usePermissionStore } from "@/stores/usePermissionStore";
import {
  buildManagerPermissionGrants,
  getDefaultAccessMode,
  getPublicModeHint,
} from "@/utils/permissionDefaults";
import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import {
  ensureLocalDateTime,
  formatDateTimeForInput,
  localDateTimeToUtc,
} from "@/utils/dateUtils";
import {
  DEFAULT_PROGRESS,
  progressShortLabel,
  progressFullLabel,
} from "@/utils/activityProgress";
import RichTextEditor from "../Generics/RichTextEditor.vue";
import { useTextStore } from "@/stores/useTextStore";
import AddPermissionForm from "../Generics/AddPermissionForm.vue";
import { useDraggableModal } from "@/composables/useDraggableModal";

const activityStore = useActivityStore();
const userStore = useUserStore();
const groupStore = useGroupStore();
const permissionStore = usePermissionStore();

const emit = defineEmits(["close", "refresh"]);

const obj = ref({
  title: "Anotações gerais",
  startedAt: formatDateTimeForInput(new Date()),
  endedAt: "",
  plannedStartAt: "",
  plannedEndAt: "",
  status: DEFAULT_PROGRESS,
  permissions: [],
  description: "",
  active: true,
});
const isPlanned = ref(false);

watch(isPlanned, (enabled) => {
  if (enabled) {
    obj.value.startedAt = "";
    const defaultPlannedDate = formatDateTimeForInput(new Date());
    if (!obj.value.plannedStartAt) obj.value.plannedStartAt = defaultPlannedDate;
    if (!obj.value.plannedEndAt) obj.value.plannedEndAt = defaultPlannedDate;
    return;
  }
  obj.value.plannedStartAt = "";
  obj.value.plannedEndAt = "";
  if (!obj.value.startedAt) obj.value.startedAt = formatDateTimeForInput(new Date());
});

// submit
const isSubmitting = ref(false);

// 🔹 NOVO: arrastar o modal pelo cabeçalho (posição não persiste entre aberturas)
const { panelRef, panelStyle, isDragging, isDraggable, onHandlePointerDown } =
  useDraggableModal();

// 🔹 NOVO: expandir/colapsar a descrição (espelho do AddNoteForm)
const isBodyExpanded = ref(false);
function handleToggleBodyExpand() {
  if (isSubmitting.value) return;
  isBodyExpanded.value = !isBodyExpanded.value;
}

// ====================================================================
// TOAST
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error");

// Ao criar com sucesso, esconde só o painel do modal imediatamente (o componente
// segue montado até o emit("close"), então o toast — que fica num Teleport próprio —
// continua visível sozinho, sem o modal sobreposto atrás.
const hideBackdropForSuccess = ref(false);

function resetToastTimeout() {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
  toastTimeoutId.value = setTimeout(() => {
    showToast.value = false;
    toastTimeoutId.value = null;
  }, 2000);
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout();
}

// ====================================================================
// COMPUTEDS
// ====================================================================
const loggedUser = computed(() => userStore.currentUser);

const creatorUserId = computed(() => loggedUser.value?.userId ?? null);
const creatorUserName = computed(() => loggedUser.value?.userName ?? "");
const createProgressLabel = computed(() =>
  isPlanned.value ? "Futura" : progressShortLabel(obj.value.status)
);

const userListForChild = computed(() =>
  (userStore.users || []).map((u) => ({
    id: u.userId,
    name: u.userName,
    photoUrl:
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      u.userPhotoPath ??
      u.user_photo_path ??
      null,
    photoPath: u.photoPath ?? u.photo_path ?? u.photoUrl ?? u.photo_url ?? null,
    userPhotoPath:
      u.userPhotoPath ??
      u.user_photo_path ??
      u.photoUrl ??
      u.photo_url ??
      u.photoPath ??
      u.photo_path ??
      null,
  }))
);

const groupListForChild = computed(() =>
  (groupStore.groups || []).map((g) => ({
    id: g.groupId,
    name: g.groupName,
    createdByUserId: g.createdByUserId ?? g.CreatedByUserId ?? null,
    createdByUserName: g.createdByUserName ?? g.CreatedByUserName ?? null,
    // Membros do grupo: o AddPermissionForm usa para avisar quando a linha de
    // um usuário substitui a permissão que ele teria pelo grupo (precedência).
    members: g.users ?? g.Users ?? [],
  }))
);

const permissionListForChild = computed(() =>
  (permissionStore.permissions || []).map((p) => ({
    id: p.permissionId,
    name: p.permissionName,
  }))
);

// ====================================================================
// HELPERS DE PERMISSÃO
// ====================================================================
function getTodosGroupId() {
  // Preferência: o "Todos" DO CÍRCULO — grupo mantido pelo sistema com o titular
  // e seus membros ativos. Sem círculo (usuário individual), cai no grupo global
  // da plataforma, como antes.
  const circleDefault = groupStore.groups.find(
    (g) => g.isCircleDefault === true
  );
  if (circleDefault) return circleDefault.groupId ?? null;

  return (
    groupStore.groups.find((g) => g.groupName?.toLowerCase() === "todos")
      ?.groupId ?? null
  );
}

function getPermissionIdByName(name) {
  return (
    permissionStore.permissions.find(
      (p) => p.permissionName?.toLowerCase() === name.toLowerCase()
    )?.permissionId ?? null
  );
}

function buildPublicPermissions() {
  const todosId = getTodosGroupId();
  const creatorId = creatorUserId.value;

  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");

  if (!creatorId || !visualizarId || !selecionarId || !editarId) {
    console.warn(
      "[AddActivityForm] Não foi possível montar permissões públicas padrão",
      { todosId, creatorId, visualizarId, selecionarId, editarId }
    );
  }

  const perms = [];

  if (todosId && visualizarId && selecionarId) {
    perms.push(
      { entityId: todosId, type: "group", permissionId: visualizarId },
      { entityId: todosId, type: "group", permissionId: selecionarId }
    );
  }

  if (creatorId && visualizarId && selecionarId && editarId) {
    perms.push(
      { entityId: creatorId, type: "user", permissionId: visualizarId },
      { entityId: creatorId, type: "user", permissionId: selecionarId },
      { entityId: creatorId, type: "user", permissionId: editarId }
    );
  }

  // Contexto de membro gerenciado: o titular acompanha o criador em tudo
  // que o membro publica (ver utils/permissionDefaults.js).
  perms.push(
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId })
  );

  return perms;
}

function buildPrivatePermissions() {
  const creatorId = creatorUserId.value;

  const visualizarId = getPermissionIdByName("visualizar");
  const selecionarId = getPermissionIdByName("selecionar");
  const editarId = getPermissionIdByName("editar");

  if (!creatorId || !visualizarId || !selecionarId || !editarId) {
    console.warn(
      "[AddActivityForm] Não foi possível montar permissões privadas",
      { creatorId, visualizarId, selecionarId, editarId }
    );
    return [];
  }

  return [
    { entityId: creatorId, type: "user", permissionId: visualizarId },
    { entityId: creatorId, type: "user", permissionId: selecionarId },
    { entityId: creatorId, type: "user", permissionId: editarId },
    ...buildManagerPermissionGrants({ visualizarId, selecionarId, editarId }),
  ];
}

// Molde com que o formulário abre — regra em utils/permissionDefaults.js
// (getDefaultAccessMode): fechado para quem tem círculo (membro ou titular),
// público só para quem não tem.
function buildInitialPermissions() {
  return getDefaultAccessMode() === "private"
    ? buildPrivatePermissions()
    : buildPublicPermissions();
}

// Compara dois arrays de permissões ignorando ordem
function permsMatch(a, b) {
  if (a.length !== b.length) return false;
  const key = (p) => `${p.type}|${p.entityId}|${p.permissionId}`;
  const setA = new Set(a.map(key));
  return b.every((p) => setA.has(key(p)));
}

// ====================================================================
// currentAccessMode — computed de 3 estados (comparação exata)
// ====================================================================
const currentAccessMode = computed(() => {
  const perms = obj.value.permissions || [];

  if (perms.length === 0) return "public";

  const publicPerms = buildPublicPermissions();
  if (publicPerms.length > 0 && permsMatch(perms, publicPerms)) return "public";

  const privatePerms = buildPrivatePermissions();
  if (privatePerms.length > 0 && permsMatch(perms, privatePerms))
    return "private";

  return "custom";
});

// ====================================================================
// selectAccessMode — handler do segmented control
// ====================================================================
function selectAccessMode(mode) {
  if (isSubmitting.value) return;

  if (mode === "public") {
    obj.value.permissions = buildPublicPermissions();
  } else if (mode === "private") {
    obj.value.permissions = buildPrivatePermissions();
  } else if (mode === "custom") {
    openPermissionModal();
  }
}

// ====================================================================
// Mini-modal de permissões
// ====================================================================
const showPermissionModal = ref(false);
const permissionFormKey = ref(0);
const permissionBackup = ref([]);
const permissionFormRef = ref(null);

// 🔹 NOVO: arrasto próprio do mini-modal, independente do painel principal.
// O v-if recria o DOM a cada abertura, mas o offset vive aqui — daí o reset.
const {
  panelRef: permPanelRef,
  panelStyle: permPanelStyle,
  isDragging: isPermDragging,
  isDraggable: isPermDraggable,
  onHandlePointerDown: onPermHandlePointerDown,
  resetPosition: resetPermPosition,
} = useDraggableModal();

watch(showPermissionModal, (val) => {
  if (val) resetPermPosition();
});

function openPermissionModal() {
  permissionBackup.value = (obj.value.permissions || []).map((p) => ({ ...p }));

  if (!obj.value.permissions || obj.value.permissions.length === 0) {
    obj.value.permissions = buildPublicPermissions();
  }

  showPermissionModal.value = true;
}

function closePermissionModal() {
  showPermissionModal.value = false;
}

function cancelPermissionModal() {
  obj.value.permissions = (permissionBackup.value || []).map((p) => ({ ...p }));
  showPermissionModal.value = false;
}

function handleConfirmPermissionModal() {
  const child = permissionFormRef.value;

  if (child && typeof child.validateBeforeConfirm === "function") {
    const isOk = child.validateBeforeConfirm();
    if (!isOk) return;
  }

  closePermissionModal();
}

// ====================================================================
// NAV / CLOSE
// ====================================================================
const closeModal = () => emit("close");

// ====================================================================
// SUBMIT
// ====================================================================
async function cadastrar() {
  if (isSubmitting.value) return;

  const textStore = useTextStore();
  const textId = textStore.idText;

  if (!textId) {
    showErrorToast("Erro: Nenhum texto selecionado.");
    return;
  }

  if (isPlanned.value) {
    if (!obj.value.plannedStartAt) {
      showErrorToast("Informe a data de início prevista.");
      return;
    }
    const plannedStart = new Date(obj.value.plannedStartAt);
    const plannedEnd = obj.value.plannedEndAt
      ? new Date(obj.value.plannedEndAt)
      : null;
    if (plannedStart <= new Date()) {
      showErrorToast("O início previsto deve ser uma data futura.");
      return;
    }
    if (plannedEnd && plannedEnd < plannedStart) {
      showErrorToast("O fim previsto não pode ser anterior ao início previsto.");
      return;
    }
  }

  if (currentAccessMode.value === "public") {
    if (!obj.value.permissions || obj.value.permissions.length === 0) {
      obj.value.permissions = buildPublicPermissions();
    }
  }

  const payload = {
    ...obj.value,
    textId,
    startedAt: localDateTimeToUtc(ensureLocalDateTime(obj.value.startedAt)),
    endedAt: localDateTimeToUtc(ensureLocalDateTime(obj.value.endedAt)),
    plannedStartAt: localDateTimeToUtc(ensureLocalDateTime(obj.value.plannedStartAt)),
    plannedEndAt: localDateTimeToUtc(ensureLocalDateTime(obj.value.plannedEndAt)),
  };

  try {
    isSubmitting.value = true;

    await activityStore.createActivity(payload);

    showSuccessToast("Atividade criada com sucesso.");

    // Esconde o painel do modal já — o toast segue visível sozinho até o
    // emit("close") abaixo, que desmonta o componente após o tempo do toast.
    hideBackdropForSuccess.value = true;

    setTimeout(() => {
      isSubmitting.value = false;
      emit("refresh");
      emit("close");
    }, 2000);
  } catch (error) {
    let msg = "Erro ao cadastrar atividade.";

    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat().join("\n");
    } else if (error.response?.data?.message) {
      msg = error.response.data.message;
    }

    showErrorToast(msg);
    isSubmitting.value = false;
  }
}

// ====================================================================
// KEYBOARD
// ====================================================================
const handleEscKey = (event) => {
  if (event.key === "Escape" && !isSubmitting.value) {
    emit("close");
  }
};

const handleEnterKey = (event) => {
  if (event.key !== "Enter") return;

  const el = event.target;
  const tag = (el?.tagName || "").toLowerCase();
  const isEditable = !!el?.isContentEditable;

  if (tag === "textarea" || isEditable) return;
  if (tag === "button" || tag === "a") return;

  if (!isSubmitting.value) {
    event.preventDefault();
    cadastrar();
  }
};

// ====================================================================
// MOUNT / UNMOUNT
// ====================================================================
onMounted(async () => {
  await Promise.all([
    userStore.fetchUsers(1, 1000, ""),
    groupStore.fetchGroups(1, 1000, ""),
    permissionStore.fetchAllPermissions(),
  ]);

  obj.value.permissions = buildInitialPermissions();

  document.addEventListener("keydown", handleEscKey);
  document.addEventListener("keydown", handleEnterKey);
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("keydown", handleEnterKey);

  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});
</script>

<template>
  <div
    v-show="!hideBackdropForSuccess"
    class="modal-backdrop"
    @click.self="!isSubmitting"
  >
    <div ref="panelRef" class="modal-panel" :style="panelStyle">
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="modal-title">Adicionar Nova Atividade</h3>
        <button
          @click="closeModal"
          type="button"
          class="modal-close-btn"
          :disabled="isSubmitting"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </button>
      </div>

      <form @submit.prevent="cadastrar" novalidate class="modal-form">
        <div class="modal-body" :class="{ 'modal-body--expanded': isBodyExpanded }">
          <div
            class="step-content"
            :class="{ 'step-content--expanded': isBodyExpanded }"
          >
            <!-- 🔹 ALTERADO: oculta o toggle quando a descrição está expandida -->
            <div class="form-group-toggle" v-show="!isBodyExpanded">
              <label for="active" class="form-label">Atividade Ativa</label>
              <label class="toggle-switch">
                <input v-model="obj.active" type="checkbox" id="active" />
                <span class="slider"></span>
              </label>
            </div>

            <div
              class="form-group"
              style="margin-top: -2.5rem"
              v-show="!isBodyExpanded"
            >
              <label for="title" class="form-label">Título</label>
              <input
                v-model="obj.title"
                type="text"
                id="title"
                class="input-base"
                placeholder="Digite o título da atividade"
                required
                @focus="$event.target.select()"
              />
            </div>

            <div
              class="form-group"
              :class="{ 'form-group--desc-expanded': isBodyExpanded }"
            >
              <!-- 🔹 ALTERADO: label agora compartilha a linha com o botão de expandir/colapsar -->
              <div class="body-label-row">
                <label for="description" class="form-label"
                  >Descrição (opcional)</label
                >

                <!-- 🔹 NOVO: botão expandir/colapsar (espelho do AddNoteForm) -->
                <button
                  type="button"
                  class="ct-expand-btn-inline"
                  :disabled="isSubmitting"
                  @click="handleToggleBodyExpand"
                  :aria-label="
                    isBodyExpanded
                      ? 'Colapsar descrição'
                      : 'Expandir descrição'
                  "
                  :title="
                    isBodyExpanded
                      ? 'Colapsar descrição'
                      : 'Expandir descrição'
                  "
                >
                  <i
                    v-if="!isBodyExpanded"
                    class="fa fa-expand"
                    aria-hidden="true"
                  ></i>
                  <i v-else class="fa fa-compress" aria-hidden="true"></i>
                </button>
              </div>
              <RichTextEditor
                v-model="obj.description"
                placeholder="Descreva sua atividade"
                :max-length="5000"
                :unwrap-pasted-line-breaks="true"
                :start-collapsed="true"
              />
            </div>

            <div class="activity-timing-row" v-show="!isBodyExpanded">
              <div class="form-group">
                <label class="form-label">Progresso</label>
                <span
                  class="progress-chip"
                  :class="{ 'progress-chip--future': isPlanned }"
                  :title="isPlanned ? 'Atividade futura' : progressFullLabel(obj.status)"
                  >{{ createProgressLabel }}</span
                >
              </div>

              <div class="form-group planning-control">
                <label for="futurePlanning" class="form-label">Planejar data futura</label>
                <label class="toggle-switch planning-toggle-switch">
                  <input
                    id="futurePlanning"
                    v-model="isPlanned"
                    type="checkbox"
                  />
                  <span class="slider"></span>
                </label>
              </div>

              <div v-if="!isPlanned" class="form-group timing-date-field">
                <label for="startDate" class="form-label">Início efetivo</label>
                <input
                  v-model="obj.startedAt"
                  type="datetime-local"
                  id="startDate"
                  class="input-base"
                  @change="obj.startedAt = ensureLocalDateTime(obj.startedAt)"
                />
              </div>
              <div v-else class="form-group timing-date-field">
                  <label for="plannedStartDate" class="form-label">Início previsto</label>
                  <input v-model="obj.plannedStartAt" type="datetime-local" id="plannedStartDate" class="input-base" required @change="obj.plannedStartAt = ensureLocalDateTime(obj.plannedStartAt)" />
              </div>

              <div class="form-group timing-date-field">
                <label for="plannedEndDate" class="form-label">Fim previsto</label>
                <input
                  v-if="isPlanned"
                  v-model="obj.plannedEndAt"
                  type="datetime-local"
                  id="plannedEndDate"
                  class="input-base"
                  :min="obj.plannedStartAt || undefined"
                  title="Opcional"
                  @change="obj.plannedEndAt = ensureLocalDateTime(obj.plannedEndAt)"
                />
                <input
                  v-else
                  type="datetime-local"
                  class="input-base timing-disabled-input"
                  aria-label="Fim previsto indisponível"
                  disabled
                />
              </div>
            </div>

            <!-- ✅ Segmented control de acesso -->
            <!-- 🔹 ALTERADO: oculta o controle de acesso quando a descrição está expandida -->
            <div class="form-group" v-show="!isBodyExpanded">
              <label class="form-label">Quem pode ver esta atividade?</label>

              <div class="access-segment">
                <!-- Público -->
                <button
                  type="button"
                  class="segment-btn"
                  :class="{
 'segment-btn--active': currentAccessMode === 'public',
                    'segment-btn--public': currentAccessMode === 'public',
                  }"
                  :disabled="isSubmitting"
                  @click="selectAccessMode('public')"
                >
                  <i class="fas fa-globe-americas segment-btn__icon"></i>
                  <span class="segment-btn__label">Público</span>
                  <!-- <span class="segment-btn__desc">Todos os usuários</span> -->
                </button>

                <!-- Privado -->
                <button
                  type="button"
                  class="segment-btn"
                  :class="{
 'segment-btn--active': currentAccessMode === 'private',
                    'segment-btn--private': currentAccessMode === 'private',
                  }"
                  :disabled="isSubmitting"
                  @click="selectAccessMode('private')"
                >
                  <i class="fas fa-lock segment-btn__icon"></i>
                  <span class="segment-btn__label">Privado</span>
                  <!-- <span class="segment-btn__desc">Só você</span> -->
                </button>

                <!-- Personalizado -->
                <button
                  type="button"
                  class="segment-btn"
                  :class="{
 'segment-btn--active': currentAccessMode === 'custom',
                    'segment-btn--custom': currentAccessMode === 'custom',
                  }"
                  :disabled="isSubmitting"
                  @click="selectAccessMode('custom')"
                >
                  <i class="fas fa-user-shield segment-btn__icon"></i>
                  <span class="segment-btn__label">Personalizado</span>
                  <!-- <span class="segment-btn__desc">Escolher usuários e grupos</span> -->
                </button>
              </div>

              <!-- Dica contextual abaixo do seletor -->
              <p class="access-hint">
                <template v-if="currentAccessMode === 'public'">
                  <i class="fas fa-info-circle"></i>
                  {{ getPublicModeHint() }} poderão visualizar e
                  selecionar esta atividade.
                </template>
                <template v-else-if="currentAccessMode === 'private'">
                  <i class="fas fa-info-circle"></i>
                  Somente você poderá acessar esta atividade.
                </template>
                <template v-else>
                  <i class="fas fa-info-circle"></i>
                  Acesso restrito às pessoas e grupos que você configurou.
                  <button
                    type="button"
                    class="access-hint__link"
                    @click="openPermissionModal"
                    :disabled="isSubmitting"
                  >
                    Editar permissões
                  </button>
                </template>
              </p>
            </div>

            <div
              style="flex-grow: 1; margin-top: -1.3rem"
              v-show="!isBodyExpanded"
            ></div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="submit"
            class="btn btn-success"
            :disabled="isSubmitting || isBodyExpanded"
          >
            <svg
              v-if="!isSubmitting"
              class="mr-1 -ml-1 w-5 h-5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clip-rule="evenodd"
              ></path>
            </svg>

            <svg
              v-else
              class="mr-2 -ml-1 w-5 h-5 animate-spin"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>

            <span>
              {{ isSubmitting ? "Adicionando..." : "Adicionar Atividade" }}
            </span>
          </button>
        </div>
      </form>
    </div>
  </div>

  <!-- Mini-modal de permissões (Teleport) -->
  <Teleport to="body">
    <transition name="permission-modal-fade">
      <div v-if="showPermissionModal" class="permission-modal-backdrop">
        <div
          ref="permPanelRef"
          class="permission-modal-panel"
          :style="permPanelStyle"
          @click.stop
        >
          <div
            class="permission-modal-header"
            :class="{
 'permission-modal-header--draggable': isPermDraggable,
              'permission-modal-header--dragging': isPermDragging,
            }"
            @pointerdown="onPermHandlePointerDown"
          >
            <h4 class="permission-modal-title">Editar Permissões</h4>
            <button
              type="button"
              class="permission-modal-close"
              @click="cancelPermissionModal"
              aria-label="Fechar Editar Permissões"
            >
              <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
            </button>
          </div>

          <div class="permission-modal-body">
            <p class="permission-modal-instruction">
              Defina aqui usuários, grupos e permissões personalizadas para esta
              atividade.
            </p>

            <AddPermissionForm
              ref="permissionFormRef"
              v-model:payload="obj.permissions"
              :key="permissionFormKey"
              :users="userListForChild"
              :groups="groupListForChild"
              :permissions="permissionListForChild"
              entityType="Activity"
              :creatorUserName="creatorUserName"
            />
          </div>

          <div class="permission-modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="cancelPermissionModal"
              :disabled="isSubmitting"
            >
              Cancelar
            </button>

            <button
              type="button"
              class="btn btn-success"
              @click="handleConfirmPermissionModal"
              :disabled="isSubmitting"
            >
              Confirmar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>

  <!-- Toast -->
  <Teleport to="body">
    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[9999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-icon">
            <svg class="w-8 h-8" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" class="toast-icon-circle" />
              <path
                v-if="toastType === 'error'"
                d="M10 5v6M10 13.5v1.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
              />
              <path
                v-else
                d="M7.5 10.5 9.5 12.5 13 8.5"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Erro ao salvar" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>

          <button
            type="button"
            class="toast-close hover:bg-black/10 rounded-full p-1 transition"
            @click="showToast = false"
            aria-label="Fechar notificação"
          >
            x
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>

.modal-panel {
  height: 720px;
  max-height: calc(100vh - 20px - 2rem);
}

.modal-form {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-footer {
  bottom: 0;
}

/* =========================
   STEP CONTENT
========================= */
.step-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 0;

  /* 🔹 ALTERADO: reduz pela metade o respiro vertical antes do campo "Atividade Ativa" */
  margin-top: -0.75rem;
}

/* 🔹 NOVO: modo expandido — a descrição ocupa todo o painel */
.modal-body--expanded {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.step-content--expanded {
  flex: 1 1 auto;
  min-height: 0;
  /* sem o pull-up quando expandido, para não cortar o topo do editor */
  margin-top: 0;
}
.form-group--desc-expanded {
  flex: 1 1 auto;
  min-height: 0;
}
.form-group--desc-expanded :deep(.rte) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
}

/* A caixa do editor cresce, mas a área editável continuava presa ao teto de
   altura que o RichTextEditor aplica quando :start-collapsed — e o rodapé (o
   contador) parava junto com ela, no meio do painel, com o fundo sobrando
   embaixo. Esse teto vem de um style inline do próprio editor: só !important
   o desfaz. */
.form-group--desc-expanded :deep(.rte-content) {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none !important;
}

/* 🔹 NOVO: linha do label + botão de expandir (espelho do AddNoteForm) */
.body-label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* 🔹 NOVO: botão expandir/colapsar (mesmo visual do AddNoteForm) */
.ct-expand-btn-inline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 4px;
  border: none;
  background: var(--neutral-200);
  color: var(--text-dark);
  cursor: pointer;
  font-size: 0.8rem;
  transition: all 0.18s ease-in-out;
  flex-shrink: 0;
}
.ct-expand-btn-inline:hover:not(:disabled) {
  background: var(--neutral-400);
  transform: translateY(-1px);
}
.ct-expand-btn-inline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-group-toggle {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

select.input-base {
  width: 100%;
}

/* =========================
   SEGMENTED CONTROL DE ACESSO
========================= */
.access-segment {
  display: flex;
  gap: 0.6rem;
  margin-top: 0.25rem;
}

.segment-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;

  padding: 0.6rem 0.5rem;
  border-radius: 12px;
  border: 1.5px solid var(--neutral-200);
  background-color: var(--surface-2);
  cursor: pointer;
  transition: all 0.18s ease;

  color: var(--text-muted);
}

.segment-btn:hover:not(:disabled):not(.segment-btn--active) {
  border-color: var(--neutral-400);
  background-color: var(--surface-3);
  color: var(--text-dark);
  transform: translateY(-1px);
}

.segment-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.segment-btn__icon {
  font-size: 1.1rem;
  line-height: 1;
}

.segment-btn__label {
  font-size: 0.82rem;
  font-weight: 600;
  line-height: 1;
}

.segment-btn__desc {
  font-size: 0.7rem;
  font-weight: 400;
  line-height: 1;
  opacity: 0.8;
}

/* Estado ativo — Público (verde) */
.segment-btn--public.segment-btn--active {
  border-color: var(--success);
  background-color: var(--success-bg);
  color: var(--success-text);
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.12);
}

/* Estado ativo — Privado (âmbar) */
.segment-btn--private.segment-btn--active {
  border-color: var(--warning);
  background-color: var(--warning-bg);
  color: var(--warning-text);
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.12);
}

/* Estado ativo — Personalizado (roxo) */
.segment-btn--custom.segment-btn--active {
  border-color: var(--brand);
  background-color: var(--brand-tint);
  color: var(--text-brand);
  box-shadow: 0 2px 8px rgba(155, 132, 255, 0.15);
}

/* Dica contextual */
.access-hint {
  font-size: 0.78rem;
  color: var(--text-muted);
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.access-hint i {
  font-size: 0.75rem;
  flex-shrink: 0;
}

.access-hint__link {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--brand);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.access-hint__link:hover:not(:disabled) {
  color: #7c63f5;
}

.access-hint__link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* =========================
   TOGGLE
========================= */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 38px;
  height: 22px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: var(--neutral-200);
  transition: 0.25s;
  border-radius: 999px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  top: 3px;
  background-color: var(--surface);
  transition: 0.25s;
  border-radius: 50%;
}

.toggle-switch input:checked + .slider {
  background-color: var(--brand-light);
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(16px);
}

/* =========================
   MINI-MODAL DE PERMISSÕES
========================= */
.permission-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10100;
  background-color: rgba(15, 23, 42, 0.2);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 50px;
  padding-left: 1rem;
  padding-right: 1rem;
}

.permission-modal-panel {
  width: 590px;
  max-width: 100%;
  height: 760px;
  max-height: calc(100vh - 100px);
  background-color: var(--bg-content);
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-modal-lg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.permission-modal-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-header);
}

/* 🔹 NOVO: cabeçalho do mini-modal como alça de arrasto.
   touch-action: none entrega o gesto ao pointermove em vez da rolagem. */
.permission-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.permission-modal-header--dragging {
  cursor: grabbing;
}
/* Os botões do cabeçalho continuam sendo clique, não alça */
.permission-modal-header--draggable button {
  cursor: pointer;
}

.permission-modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.permission-modal-close {
  color: var(--text-light);
  background-color: transparent;
  border-radius: 8px;
  padding: 0.375rem;
  transition: background-color 0.2s, color 0.2s;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}
.permission-modal-close:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: var(--text-dark);
}
.permission-modal-close svg {
  width: 1.25rem;
  height: 1.25rem;
}

.permission-modal-body {
  padding: 0.8rem 1rem 0.5rem;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-content);
  min-height: 0;
  overflow: hidden;
}

.permission-modal-instruction {
  font-size: 0.82rem;
  color: var(--text-light);
  margin-bottom: 0.75rem;
  line-height: 1.35;
  display: block;
}

.permission-modal-footer {
  flex: 0 0 auto;
  padding: 0.6rem 1rem 0.75rem;
  border-top: 1px solid var(--border-color);
  background-color: var(--bg-header);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

:deep(.permission-form-wrapper) {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

:deep(.mt-5) {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  margin-top: 1rem !important;
}

:deep(.table-wrap) {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  border: 0px solid var(--neutral-200);
  border-radius: 8px;
  background: var(--bg-content);
  margin-top: 0.5rem;
}

:deep(.table-wrap::-webkit-scrollbar) {
  width: 6px;
}
:deep(.table-wrap::-webkit-scrollbar-thumb) {
  background-color: var(--neutral-400);
  border-radius: 10px;
}

.permission-modal-fade-enter-active,
.permission-modal-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.18s ease;
}
.permission-modal-fade-enter-from,
.permission-modal-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.select-progress {
  height: 34px;
  line-height: 34px;
  padding-top: 0;
  padding-bottom: 0;
}

/* ── Progresso: chip estática (atividade nova sempre "Em andamento") ─────── */
.progress-chip {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  height: 34px;
  padding: 0 0.7rem;
  border: 1px solid #f59f00;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  background-color: #fff3bf;
  color: #f59f00;
}
.progress-chip--future {
  background-color: var(--brand-soft);
  border-color: #6d28d9;
  color: #6d28d9;
}

.activity-timing-row { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); align-items: end; gap: 0.75rem; }
.activity-timing-row > * { min-width: 0; }
.planning-control { min-height: 57px; }
.planning-toggle-switch { margin-top: 6px; }
.timing-date-field .input-base { width: 100%; min-width: 0; padding-left: 0.45rem; padding-right: 0.3rem; font-size: 0.76rem; }
.timing-disabled-input:disabled {
  background-color: var(--surface-2);
  color: var(--text-muted);
  cursor: not-allowed;
  opacity: 0.55;
}
@media (max-width: 767px) {
  .activity-timing-row { grid-template-columns: 1fr; align-items: stretch; }
  .timing-date-field .input-base { font-size: 0.85rem; }
}
</style>
